import { useState, useEffect, useCallback, useMemo } from 'react'
import { resumenDia, resumenPorDia, productosMasVendidos, hoyLocal } from '../db/reportes'
import { formatearPeso, formatearFecha, formatearDia, formatearHora } from '../utils/formato'
import { useConfig } from '../context/ConfigContext'
import TourBanner from '../components/layout/TourBanner'

const LABEL_METODO = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transfer.', fiado: 'Fiado' }
const COLOR_METODO  = { efectivo: 'text-tienda-success', tarjeta: 'text-blue-400', transferencia: 'text-purple-400', fiado: 'text-tienda-danger' }
const DOT_METODO    = { efectivo: 'bg-tienda-success', tarjeta: 'bg-blue-400', transferencia: 'bg-purple-400', fiado: 'bg-tienda-danger' }

const PERIODOS = [
  { label: 'Hoy',    dias: 0 },
  { label: '7 días', dias: 7 },
  { label: '30 días',dias: 30 },
  { label: 'Todo',   dias: null },
]

const PERIODOS_HIST = [
  { label: '7 días', dias: 7 },
  { label: '30 días',dias: 30 },
  { label: '90 días',dias: 90 },
  { label: 'Todo',   dias: null },
]

export default function Reportes() {
  const [tab, setTab] = useState('hoy')

  // datos hoy
  const [resumenHoy, setResumenHoy] = useState(null)
  const [cargandoHoy, setCargandoHoy] = useState(false)
  const [ventaDetalle, setVentaDetalle] = useState(null)

  // datos historial
  const [dias, setDias] = useState(null)
  const [cargandoDias, setCargandoDias] = useState(false)
  const [diaDetalle, setDiaDetalle] = useState(null)     // 'YYYY-MM-DD' seleccionado
  const [detalleDia, setDetalleDia] = useState(null)     // resumenDia del dia seleccionado
  const [periodoHist, setPeriodoHist] = useState(1)      // index en PERIODOS_HIST (default 30d)

  // datos productos
  const [productos, setProductos] = useState(null)
  const [cargandoProductos, setCargandoProductos] = useState(false)
  const [periodo, setPeriodo] = useState(0)              // index en PERIODOS

  const cargarHoy = useCallback(async () => {
    setCargandoHoy(true)
    setResumenHoy(await resumenDia(hoyLocal()))
    setCargandoHoy(false)
  }, [])

  const cargarHistorial = useCallback(async () => {
    setCargandoDias(true)
    setDias(await resumenPorDia(PERIODOS_HIST[periodoHist].dias))
    setCargandoDias(false)
  }, [periodoHist])

  const cargarProductos = useCallback(async () => {
    setCargandoProductos(true)
    setProductos(await productosMasVendidos(PERIODOS[periodo].dias))
    setCargandoProductos(false)
  }, [periodo])

  useEffect(() => { if (tab === 'hoy') cargarHoy() }, [tab, cargarHoy])
  useEffect(() => { if (tab === 'historial') cargarHistorial() }, [tab, periodoHist, cargarHistorial])
  useEffect(() => { if (tab === 'productos') cargarProductos() }, [tab, periodo, cargarProductos])

  async function handleVerDia(fecha) {
    setDiaDetalle(fecha)
    setDetalleDia(await resumenDia(fecha))
  }

  return (
    <div className="flex-1 flex flex-col bg-tienda-bg overflow-hidden">

      {/* Cabecera */}
      <div className="pt-5 pb-0 bg-tienda-surface border-b border-tienda-border flex-shrink-0">
        <h1 className="px-4 text-2xl font-bold text-tienda-text mb-4">Mis Ventas</h1>

        {/* Tabs */}
        <div className="flex gap-0">
          {[['hoy', 'Hoy'], ['historial', 'Historial'], ['productos', 'Más Vendidos']].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors
                ${tab === id
                  ? 'border-tienda-accent text-tienda-accent'
                  : 'border-transparent text-tienda-muted'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-y-auto">

        {/* Tour banner — solo primera visita */}
        {tab === 'hoy' && (
          <TourBanner
            moduleKey="misVentas"
            titulo="Aquí ves tus ventas"
            descripcion="Consulta el resumen del día, el historial de los últimos 30 días y los productos más vendidos."
          />
        )}

        {/* ── HOY ── */}
        {tab === 'hoy' && (
          cargandoHoy || !resumenHoy
            ? <Spinner />
            : <TabHoy data={resumenHoy} onRecargar={cargarHoy} onVerVenta={setVentaDetalle} />
        )}

        {/* ── HISTORIAL ── */}
        {tab === 'historial' && (
          <TabHistorial
            dias={dias}
            cargando={cargandoDias}
            periodo={periodoHist}
            onPeriodo={setPeriodoHist}
            onVerDia={handleVerDia}
          />
        )}

        {/* ── PRODUCTOS ── */}
        {tab === 'productos' && (
          <TabProductos
            productos={productos}
            cargando={cargandoProductos}
            periodo={periodo}
            onPeriodo={setPeriodo}
          />
        )}
      </div>

      {/* Modal detalle día */}
      {diaDetalle && (
        <ModalDetalleDia
          fecha={diaDetalle}
          data={detalleDia}
          onCerrar={() => { setDiaDetalle(null); setDetalleDia(null) }}
          onVerVenta={setVentaDetalle}
        />
      )}

      {ventaDetalle && (
        <ModalTicketDetalle venta={ventaDetalle} onCerrar={() => setVentaDetalle(null)} />
      )}
    </div>
  )
}

// ── TAB HOY ─────────────────────────────────────────────────────

function TabHoy({ data, onRecargar, onVerVenta }) {
  const [filtroUsuario, setFiltroUsuario] = useState(null)

  // Usuarios distintos que vendieron hoy (solo los que tienen nombre registrado)
  const usuarios = useMemo(() =>
    [...new Set(data.ventas.map(v => v.usuarioNombre).filter(Boolean))].sort()
  , [data.ventas])

  const multiUsuario = usuarios.length > 1

  const ventasFiltradas = filtroUsuario
    ? data.ventas.filter(v => v.usuarioNombre === filtroUsuario)
    : data.ventas

  // Totales por usuario (solo si hay más de uno)
  const porUsuario = useMemo(() => {
    if (!multiUsuario) return null
    const acc = {}
    for (const v of data.ventas) {
      const u = v.usuarioNombre || 'Sin asignar'
      if (!acc[u]) acc[u] = { total: 0, count: 0 }
      acc[u].total += v.total
      acc[u].count++
    }
    return Object.entries(acc).sort(([, a], [, b]) => b.total - a.total)
  }, [data.ventas, multiUsuario])

  return (
    <div className="p-4 flex flex-col gap-4">

      {/* Hero total */}
      <div className="card p-5">
        <div className="flex items-start justify-between mb-1">
          <p className="text-tienda-muted text-sm">Total del día</p>
          <button onClick={onRecargar} className="text-tienda-muted active:text-tienda-accent transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
        <p className={`text-5xl font-bold ${data.total > 0 ? 'text-tienda-success' : 'text-tienda-muted'}`}>
          {formatearPeso(data.total)}
        </p>
        <div className="flex gap-4 mt-3">
          <Stat label="Ventas" value={data.count} />
          <Stat label="Promedio" value={data.count > 0 ? formatearPeso(data.promedio) : '—'} />
          <Stat label="Fiados" value={data.porMetodo.fiado.count} danger={data.porMetodo.fiado.count > 0} />
        </div>

        {/* Desglose por vendedor (solo cuando hay más de uno) */}
        {porUsuario && (
          <div className="mt-4 pt-4 border-t border-tienda-border space-y-2">
            <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wider mb-1">Por vendedor</p>
            {porUsuario.map(([nombre, val]) => (
              <div key={nombre} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-tienda-accent flex-shrink-0" />
                  <span className="text-tienda-text text-sm font-medium">{nombre}</span>
                  <span className="text-tienda-muted text-xs">{val.count} venta{val.count !== 1 ? 's' : ''}</span>
                </div>
                <span className="text-tienda-success font-bold text-sm">{formatearPeso(val.total)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filtro por usuario (solo cuando hay más de uno) */}
      {multiUsuario && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFiltroUsuario(null)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors
              ${!filtroUsuario
                ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
          >
            Todos
          </button>
          {usuarios.map(u => (
            <button
              key={u}
              onClick={() => setFiltroUsuario(u === filtroUsuario ? null : u)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors
                ${filtroUsuario === u
                  ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                  : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
            >
              {u}
            </button>
          ))}
        </div>
      )}

      {/* Desglose por método */}
      {data.count > 0 && (
        <div className="card overflow-hidden">
          <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wider px-4 pt-4 pb-2">
            Por forma de pago
          </p>
          {Object.entries(data.porMetodo)
            .filter(([, v]) => v.count > 0)
            .sort(([, a], [, b]) => b.total - a.total)
            .map(([metodo, val]) => (
              <div key={metodo} className="flex items-center justify-between px-4 py-3 border-t border-tienda-border first:border-0">
                <div className="flex items-center gap-3">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT_METODO[metodo] ?? 'bg-tienda-muted'}`} />
                  <span className="text-tienda-text font-semibold">{LABEL_METODO[metodo] ?? metodo}</span>
                  <span className="text-tienda-muted text-sm">{val.count} venta{val.count !== 1 ? 's' : ''}</span>
                </div>
                <span className={`font-bold text-base ${COLOR_METODO[metodo] ?? 'text-tienda-text'}`}>
                  {formatearPeso(val.total)}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Lista de ventas del día */}
      {ventasFiltradas.length > 0 ? (
        <div>
          <p className="text-tienda-muted text-xs font-semibold uppercase tracking-wider mb-2">
            {filtroUsuario ? `Ventas de ${filtroUsuario}` : 'Ventas de hoy'}
          </p>
          <div className="space-y-2">
            {ventasFiltradas.map(v => (
              <VentaFila key={v._id} venta={v} multiUsuario={multiUsuario} onClick={() => onVerVenta(v)} />
            ))}
          </div>
        </div>
      ) : (
        <SinDatos mensaje="Sin ventas registradas hoy" onRecargar={onRecargar} />
      )}
    </div>
  )
}

// ── TAB HISTORIAL ────────────────────────────────────────────────

function TabHistorial({ dias, cargando, periodo, onPeriodo, onVerDia }) {
  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Selector de período */}
      <div className="flex gap-2">
        {PERIODOS_HIST.map((p, i) => (
          <button
            key={i}
            onClick={() => onPeriodo(i)}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors
              ${periodo === i
                ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {cargando || !dias ? (
        <Spinner />
      ) : dias.length === 0 ? (
        <SinDatos mensaje="Sin ventas en este período" />
      ) : (
        <TabHistorialContenido dias={dias} onVerDia={onVerDia} />
      )}
    </div>
  )
}

function TabHistorialContenido({ dias, onVerDia }) {
  const totalGeneral = dias.reduce((s, d) => s + d.total, 0)
  const promediodia  = dias.length > 0 ? totalGeneral / dias.length : 0

  return (
    <>
      {/* Resumen global */}
      <div className="card p-4 flex gap-4">
        <div className="flex-1">
          <p className="text-tienda-muted text-xs mb-1">{dias.length} día{dias.length !== 1 ? 's' : ''} con ventas</p>
          <p className="text-tienda-success font-bold text-2xl">{formatearPeso(totalGeneral)}</p>
        </div>
        <div className="flex-1">
          <p className="text-tienda-muted text-xs mb-1">Promedio por día</p>
          <p className="text-tienda-text font-bold text-2xl">{formatearPeso(promediodia)}</p>
        </div>
      </div>

      {/* Lista de días */}
      <div className="space-y-2">
        {dias.map(d => (
          <button
            key={d.fecha}
            onClick={() => onVerDia(d.fecha)}
            className="card w-full flex items-center justify-between px-4 py-3.5 text-left active:scale-[0.98] transition-transform"
          >
            <div>
              <p className="text-tienda-text font-semibold">{formatearDia(d.fecha)}</p>
              <p className="text-tienda-muted text-sm">
                {d.count} venta{d.count !== 1 ? 's' : ''}
                {d.fiados > 0 && <span className="text-tienda-danger ml-2">· {d.fiados} fiado{d.fiados !== 1 ? 's' : ''}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-tienda-success font-bold text-lg">{formatearPeso(d.total)}</span>
              <svg className="w-4 h-4 text-tienda-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          </button>
        ))}
      </div>
    </>
  )
}

// ── TAB PRODUCTOS ────────────────────────────────────────────────

function TabProductos({ productos, cargando, periodo, onPeriodo }) {
  const max = productos?.[0]?.cantidad ?? 1

  return (
    <div className="p-4 flex flex-col gap-4">
      {/* Selector de período */}
      <div className="flex gap-2">
        {PERIODOS.map((p, i) => (
          <button
            key={i}
            onClick={() => onPeriodo(i)}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold border transition-colors
              ${periodo === i
                ? 'bg-tienda-accent text-gray-900 border-tienda-accent'
                : 'bg-tienda-bg border-tienda-border text-tienda-muted'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {cargando || !productos ? (
        <Spinner />
      ) : productos.length === 0 ? (
        <SinDatos mensaje="Sin ventas en este período" />
      ) : (
        <div className="space-y-2">
          {productos.map((p, i) => (
            <div key={p._id} className="card px-4 py-3">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-tienda-muted text-sm font-bold w-5 text-center flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-tienda-text font-semibold truncate">{p.nombre}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-tienda-success font-bold">{formatearPeso(p.total)}</p>
                  <p className="text-tienda-muted text-xs">{p.cantidad} uds</p>
                </div>
              </div>
              {/* Barra relativa */}
              <div className="ml-8 h-1.5 bg-tienda-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-tienda-accent rounded-full"
                  style={{ width: `${Math.round((p.cantidad / max) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── MODAL DETALLE DÍA ────────────────────────────────────────────

function ModalDetalleDia({ fecha, data, onCerrar, onVerVenta }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-tienda-surface w-full max-w-lg rounded-t-3xl overflow-hidden flex flex-col max-h-[85vh]">

        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-tienda-border flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-tienda-text">{formatearDia(fecha)}</h2>
            {data && (
              <p className="text-tienda-muted text-sm">
                {data.count} venta{data.count !== 1 ? 's' : ''} · {formatearPeso(data.total)}
              </p>
            )}
          </div>
          <button onClick={onCerrar} className="w-9 h-9 flex items-center justify-center text-tienda-muted">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!data ? (
            <Spinner />
          ) : data.ventas.length === 0 ? (
            <p className="text-tienda-muted text-sm text-center py-8">Sin ventas este día</p>
          ) : (() => {
            const multiUsuario = new Set(data.ventas.map(v => v.usuarioNombre).filter(Boolean)).size > 1
            return (
              <div className="space-y-2">
                {data.ventas.map(v => (
                  <VentaFila key={v._id} venta={v} multiUsuario={multiUsuario} onClick={() => onVerVenta(v)} />
                ))}
              </div>
            )
          })()}
        </div>
      </div>
    </div>
  )
}

// ── HELPERS ──────────────────────────────────────────────────────

function VentaFila({ venta, onClick, multiUsuario }) {
  const metodo = venta.metodoPago || 'efectivo'
  const Tag = onClick ? 'button' : 'div'
  return (
    <Tag
      onClick={onClick}
      className={`card w-full px-4 py-3 flex items-center justify-between gap-3 text-left
        ${onClick ? 'active:scale-[0.98] transition-transform' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${DOT_METODO[metodo] ?? 'bg-tienda-muted'}`} />
        <div className="min-w-0">
          <p className="text-tienda-text text-sm font-semibold truncate">
            {venta.items?.length ?? 0} artículo{(venta.items?.length ?? 0) !== 1 ? 's' : ''}
            {venta.clienteNombre && <span className="text-tienda-danger"> · {venta.clienteNombre}</span>}
          </p>
          <p className="text-tienda-muted text-xs">
            {LABEL_METODO[metodo] ?? metodo} · {formatearHora(venta.fecha)}
            {multiUsuario && venta.usuarioNombre && (
              <span className="ml-1.5 bg-tienda-surface border border-tienda-border text-tienda-muted rounded px-1.5 py-px text-[10px] font-medium">
                {venta.usuarioNombre}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-tienda-text font-bold">{formatearPeso(venta.total)}</span>
        {onClick && (
          <svg className="w-4 h-4 text-tienda-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" />
          </svg>
        )}
      </div>
    </Tag>
  )
}

// ── MODAL TICKET DETALLE ─────────────────────────────────────────

function ModalTicketDetalle({ venta, onCerrar }) {
  const folio = 'PJ-' + venta._id.slice(-6).toUpperCase()
  const { config } = useConfig()
  const metodoLabel = { efectivo: 'Efectivo', tarjeta: 'Tarjeta', transferencia: 'Transferencia', fiado: 'Fiado' }[venta.metodoPago] ?? venta.metodoPago
  const esFiado = venta.metodoPago === 'fiado'

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-tienda-surface rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl">

        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-tienda-border">
          <div>
            <h2 className="text-xl font-bold text-tienda-text">Ticket</h2>
            <p className="text-tienda-muted text-sm">{formatearFecha(venta.fecha)}</p>
          </div>
          <button onClick={onCerrar} className="w-9 h-9 flex items-center justify-center text-tienda-muted">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          <div className="bg-tienda-bg rounded-2xl p-4 font-mono text-sm space-y-1">
            <div className="text-center mb-3">
              <p className="text-tienda-text font-bold text-base">{config.nombreTienda}</p>
              <p className="text-tienda-muted text-xs">Folio: {folio}</p>
            </div>
            <div className="border-t border-dashed border-tienda-border pt-2 space-y-1">
              {venta.items?.map(item => {
                const esKg = item.unidad === 'kg'
                // En DB: precio kg = precioFinal (ya es el total), no multiplicar por cantidad
                const precioItem = esKg ? item.precio : item.precio * item.cantidad
                return (
                  <div key={item._id} className="flex justify-between gap-2">
                    <span className="text-tienda-muted truncate">
                      {esKg ? `${item.cantidad}kg` : `${item.cantidad}x`} {item.nombre}
                    </span>
                    <span className="text-tienda-text flex-shrink-0">{formatearPeso(precioItem)}</span>
                  </div>
                )
              })}
            </div>
            <div className="border-t border-dashed border-tienda-border pt-2 flex justify-between">
              <span className="font-bold text-tienda-text">TOTAL</span>
              <span className="font-bold text-tienda-accent text-base">{formatearPeso(venta.total)}</span>
            </div>
            <div className="border-t border-dashed border-tienda-border pt-2 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-tienda-muted">Forma de pago</span>
                <span className="text-tienda-text font-semibold">{metodoLabel}</span>
              </div>
              {esFiado && venta.clienteNombre && (
                <div className="flex justify-between text-xs">
                  <span className="text-tienda-muted">Cliente</span>
                  <span className="text-tienda-danger font-bold">{venta.clienteNombre}</span>
                </div>
              )}
            </div>
            <p className="text-center text-tienda-muted text-xs pt-2">Gracias por su compra</p>
          </div>
        </div>

      </div>
    </div>
  )
}

function Stat({ label, value, danger }) {
  return (
    <div>
      <p className="text-tienda-muted text-xs">{label}</p>
      <p className={`font-bold text-lg ${danger ? 'text-tienda-danger' : 'text-tienda-text'}`}>{value}</p>
    </div>
  )
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="w-8 h-8 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function SinDatos({ mensaje, onRecargar }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <div className="w-16 h-16 rounded-2xl bg-tienda-surface border border-tienda-border flex items-center justify-center text-3xl">
        📊
      </div>
      <p className="text-tienda-muted">{mensaje}</p>
      {onRecargar && (
        <button onClick={onRecargar} className="btn-ghost text-sm px-4 py-2">
          Recargar
        </button>
      )}
    </div>
  )
}
